(() => {
  // Require an explicit offset so the same campaign ends together in every market.
  function parseDeadline(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value || '');
    if (!match) return NaN;
    const [, year, month, day, hour, minute, second, zone] = match;
    const local = new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, +second));
    if (local.getUTCFullYear() !== +year || local.getUTCMonth() !== +month - 1 || local.getUTCDate() !== +day || +hour > 23 || +minute > 59 || +second > 59) return NaN;
    if (zone !== 'Z' && (+zone.slice(1, 3) > 14 || +zone.slice(4) > 59 || (+zone.slice(1, 3) === 14 && +zone.slice(4) !== 0))) return NaN;
    return Date.parse(value);
  }

  class SkrtSlideCountdown extends HTMLElement {
    static get observedAttributes() { return ['data-expires']; }
    connectedCallback() {
      this.onVisible = () => { this.update(); this.scheduleFit(); };
      this.overlay = this.closest('.skrt-hero__overlay');
      this.originalTopPadding = this.overlay?.style.paddingTop || '';
      this.originalBottomPadding = this.overlay?.style.paddingBottom || '';
      this.onFit = () => this.scheduleFit();
      if (this.overlay) {
        if (typeof ResizeObserver !== 'undefined') {
          this.fitObserver = new ResizeObserver(this.onFit);
          this.fitObserver.observe(this.overlay);
          for (const node of this.overlay.children) if (node !== this) this.fitObserver.observe(node);
        }
        window.addEventListener('resize', this.onFit);
        document.fonts?.addEventListener('loadingdone', this.onFit);
        document.fonts?.ready.then(this.onFit);
      }
      document.addEventListener('visibilitychange', this.onVisible);
      this.start();
    }
    disconnectedCallback() {
      clearInterval(this.interval);
      document.removeEventListener('visibilitychange', this.onVisible);
      this.fitObserver?.disconnect();
      this.resetFit();
      if (this.overlay) {
        window.removeEventListener('resize', this.onFit);
        document.fonts?.removeEventListener('loadingdone', this.onFit);
      }
    }
    attributeChangedCallback() { if (this.isConnected) this.start(); }
    start() {
      clearInterval(this.interval);
      this.deadline = parseDeadline(this.dataset.expires);
      this.update();
      this.scheduleFit();
      if (Number.isFinite(this.deadline) && this.deadline > Date.now()) {
        this.interval = setInterval(() => this.update(), 1000);
      }
    }
    resetFit() {
      if (this.fitFrame) cancelAnimationFrame(this.fitFrame);
      this.fitFrame = 0;
      this.style.removeProperty('--skrt-countdown-fit');
      if (this.overlay) {
        this.overlay.style.paddingTop = this.originalTopPadding;
        this.overlay.style.paddingBottom = this.originalBottomPadding;
      }
    }
    scheduleFit() {
      if (!this.overlay || !this.isConnected || this.hidden || this.fitFrame) return;
      this.fitFrame = requestAnimationFrame(() => { this.fitFrame = 0; this.fit(); });
    }
    fit() {
      const overlay = this.overlay;
      const timer = this.querySelector('[data-countdown-digits]');
      if (!overlay || !timer || this.hidden || timer.hidden || !this.isConnected) return;
      // Reset our overrides so a wider or taller layout can grow back to its CSS target.
      overlay.style.paddingTop = this.originalTopPadding;
      overlay.style.paddingBottom = this.originalBottomPadding;
      this.style.removeProperty('--skrt-countdown-fit');
      const box = overlay.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const px = value => parseFloat(value) || 0;
      const layout = getComputedStyle(overlay);
      const top = px(layout.paddingTop), bottom = px(layout.paddingBottom);
      const insetTop = Math.min(12, top), insetBottom = Math.min(12, bottom);
      const width = box.width - px(layout.paddingLeft) - px(layout.paddingRight);
      const children = Array.from(overlay.children).filter(node => {
        const style = getComputedStyle(node);
        return style.display !== 'none' && style.position !== 'absolute';
      });
      const otherHeight = children.reduce((sum, node) => {
        const style = getComputedStyle(node);
        return sum + (node === this ? 0 : node.getBoundingClientRect().height)
          + px(style.marginTop) + px(style.marginBottom);
      }, 0);
      const gapHeight = Math.max(0, children.length - 1) * px(layout.rowGap);
      const height = box.height - insetTop - insetBottom - otherHeight - gapHeight;
      const target = px(getComputedStyle(this).fontSize);
      if (!(width > 0 && height > 0 && target > 0)) return;
      const setSize = size => this.style.setProperty('--skrt-countdown-fit', size.toFixed(2) + 'px');
      const fits = () => Math.max(timer.scrollWidth, timer.getBoundingClientRect().width) <= width + 0.5
        && this.getBoundingClientRect().height <= height + 0.5;
      setSize(target);
      if (!fits()) {
        let low = 0, high = target;
        for (let step = 0; step < 9; step++) {
          const mid = (low + high) / 2;
          setSize(mid);
          if (fits()) low = mid; else high = mid;
        }
        setSize(low);
      }
      // Reclaim only spare edge padding when the enlarged row would otherwise clip copy.
      const first = children[0], last = children[children.length - 1];
      if (first && layout.justifyContent === 'flex-end') {
        const overflow = box.top + insetTop - first.getBoundingClientRect().top;
        if (overflow > 0) overlay.style.paddingBottom = Math.max(insetBottom, bottom - overflow) + 'px';
      } else if (last && layout.justifyContent === 'flex-start') {
        const overflow = last.getBoundingClientRect().bottom - (box.bottom - insetBottom);
        if (overflow > 0) overlay.style.paddingTop = Math.max(insetTop, top - overflow) + 'px';
      } else if (first && last) {
        const free = Math.max(0, box.height - otherHeight - gapHeight - this.getBoundingClientRect().height);
        if (free < top + bottom) {
          const ratio = Math.max(0, free - insetTop - insetBottom) / Math.max(1, top + bottom - insetTop - insetBottom);
          overlay.style.paddingTop = (insetTop + (top - insetTop) * ratio) + 'px';
          overlay.style.paddingBottom = (insetBottom + (bottom - insetBottom) * ratio) + 'px';
        }
      }
    }
    update() {
      const valid = Number.isFinite(this.deadline);
      this.hidden = !valid;
      if (!valid) { this.resetFit(); return; }
      const remaining = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
      const expired = remaining === 0;
      this.toggleAttribute('data-expired', expired);
      const timer = this.querySelector('[data-countdown-digits]');
      const message = this.querySelector('[data-countdown-expired]');
      const label = this.querySelector('[data-countdown-label]');
      if (timer) timer.hidden = expired;
      if (label) label.hidden = expired;
      if (message) message.hidden = !expired;
      if (expired) {
        this.resetFit();
        clearInterval(this.interval);
        this.hidden = !message?.textContent.trim();
        return;
      }
      const values = [Math.floor(remaining / 86400), Math.floor(remaining / 3600) % 24, Math.floor(remaining / 60) % 60, remaining % 60];
      const digitCount = String(values[0]).padStart(2, '0').length;
      const needsFit = digitCount !== this.dayDigitCount;
      this.dayDigitCount = digitCount;
      this.querySelectorAll('[data-countdown-number]').forEach((node, index) => {
        const value = String(values[index]).padStart(2, '0');
        if (node.textContent !== value) node.textContent = value;
      });
      if (needsFit) this.scheduleFit();
    }
  }

  if (!customElements.get('skrt-slide-countdown')) customElements.define('skrt-slide-countdown', SkrtSlideCountdown);
})();
